<template>
  <div class="max-w-screen-xl">
    <div class="flex w-full mb-10 p-4 md:p-0">
      <h2 class="grow text-2xl font-bold">Dokumente</h2>
      <LSButton type="secondary" size="small" @click="router.push({ name: 'newLegalDocument' })">Neues Dokument</LSButton>
    </div>
    <div class="mb-4">
      <div class="flex items-center">
        <div
          @click="showArchived = false"
          class="cursor-pointer px-4 py-2 rounded-md border border-l-0 border-b-0 border-gray-300"
          :class="{ 'text-indigo-600 bg-white font-bold': !showArchived, 'text-gray-500 bg-gray-100': showArchived }"
        >
          Active
        </div>
        <div
          @click="showArchived = true"
          class="cursor-pointer px-4 py-2 rounded-md border border-l-0 border-b-0 border-gray-300"
          :class="{ 'text-indigo-600 bg-white font-bold': showArchived, 'text-gray-500 bg-gray-100': !showArchived }"
        >
          Archived
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-y-2">
      <table class="min-w-full bg-white table-fixed">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-200">
            <th
              class="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              @click="sortBy('name')"
            >
              Document Name
              <span v-if="sortField === 'name'" class="ml-1">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th
              class="sm:table-cell hidden px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              @click="sortBy('template_name')"
            >
              Template
              <span v-if="sortField === 'template_name'" class="ml-1">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th
              class="md:table-cell hidden px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              @click="sortBy('created_at')"
            >
              Created At
              <span v-if="sortField === 'created_at'" class="ml-1">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th
              class="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
              @click="sortBy('workflow_progress')"
            >
              Status
              <span v-if="sortField === 'workflow_progress'" class="ml-1">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <tr
            v-for="doc in sortedDocuments"
            :key="doc.id"
            @click="handleDocumentClicked(doc.id)"
            class="hover:bg-secondary-100 cursor-pointer"
            :class="{ 'bg-secondary-50': selectedDocument?.id === doc.id }"
          >
            <td class="px-6 py-4">
              <div class="flex items-center">
                <DocumentTextIcon class="h-5 w-5 text-secondary mr-2" />
                <div class="font-medium text-gray-900">{{ doc.name }}</div>
              </div>
            </td>
            <td class="px-6 py-4 text-gray-500 sm:table-cell hidden">{{ doc.template_name }}</td>
            <td class="px-6 py-4 text-gray-500 md:table-cell hidden">
              <div class="flex items-center">
                <CalendarIcon class="h-5 w-5 mr-2" />
                {{ $d(Date.parse(doc.created_at!)) }}
              </div>
            </td>
            <td class="px-6 py-4">
              <span
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                :class="{
                  'bg-orange-100 text-orange-800': doc.workflow_progress === 'draft',
                  'bg-green-100 text-green-800': doc.workflow_progress === 'finalized',
                }"
              >
                {{ doc.workflow_progress }}
              </span>
            </td>
            <td class="px-6 py-4 text-right">
              <button
                v-if="!doc.archived"
                class="text-slate-900 rounded-lg px-2 py-1 hover:bg-gray-100 transition-colors mr-2"
                @click.stop="archiveDocumentMutation.mutate(doc.id!)"
                title="Archive Document"
              >
                <ArchiveBoxArrowDownIcon class="h-4 w-4" />
              </button>
              <button
                v-else
                class="text-slate-900 rounded-lg px-2 py-1 hover:bg-gray-100 transition-colors"
                @click.stop="unarchiveDocumentMutation.mutate(doc.id!)"
                title="Unarchive Document"
              >
                <ArchiveBoxXMarkIcon class="h-4 w-4" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="selectedDocument" class="flex h-screen absolute right-0 top-0 bg-white" :style="{ width: `${paneWidth / 16}rem` }">
      <div class="bg-slate-400 w-2 cursor-col-resize" @mousedown.prevent="startResizing"></div>
      <div class="border-l-slate-400 border-l p-10 flex flex-col gap-y-4">
        <h2 class="text-3xl">{{ selectedDocument.name }}</h2>
        <ul>
          <li>
            <div class="font-bold">Document Type:</div>
            {{ selectedDocument.template_name }}
          </li>
        </ul>
        <RouterLink :to="{ name: 'legalDocument', params: { documentId: selectedDocument.id } }">
          <LSButton type="secondary" variant="outline" size="small">
            <PencilSquareIcon class="w-6 h-6"></PencilSquareIcon> Edit Document
          </LSButton>
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DocumentTextIcon, PencilSquareIcon } from '@heroicons/vue/24/solid'
import { CalendarIcon, ArchiveBoxArrowDownIcon, ArchiveBoxXMarkIcon } from '@heroicons/vue/24/outline'
import { useRouter, useRoute } from 'vue-router'
import { onMounted, ref, computed, watch } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import type { AxiosError } from 'axios'

import documentsService from '@/services/documentsService'
import { useQuestionnaireStore } from '@/questionnaire/stores/questionnaire'
import type { Document } from '@/questionnaire/types'
import LSButton from '@/components/LSButton.vue'
import { storeToRefs } from 'pinia'
import { useLsGeneralStore } from '@/questionnaire/stores/application'
import { successNotification, errorNotification } from '@/utils/notifications'

const isResized = ref(false)
const { documentPanelWidth } = storeToRefs(useLsGeneralStore())
const paneWidth = ref(documentPanelWidth.value || 400)

const handleResizing = (e: MouseEvent) => {
  if (!isResized.value) {
    return
  }
  paneWidth.value = Math.max(200, paneWidth.value - e.movementX)
}

const startResizing = () => {
  isResized.value = true
  document.addEventListener('mousemove', handleResizing)
  document.addEventListener('mouseup', stopResizing)
}

const stopResizing = () => {
  isResized.value = false
  documentPanelWidth.value = paneWidth.value
  document.removeEventListener('mousemove', handleResizing)
  document.removeEventListener('mouseup', stopResizing)
}

const store = useQuestionnaireStore()
onMounted(async () => {
  store.resetStore()
})
const router = useRouter()
const route = useRoute()
const queryClient = useQueryClient()

const showArchived = ref(route.query.archived === 'true')
const selectedDocument = ref<Document>()

watch(showArchived, () => {
  queryClient.invalidateQueries({ queryKey: ['documents'] })
  router.push({ name: 'home', query: { archived: showArchived.value ? 'true' : 'false' } })
})

const handleDocumentClicked = (documentId: string) => {
  if (selectedDocument.value?.id !== documentId) {
    // console.log(typeof documents.value)
    const document = documents.value?.find(doc => doc.id === documentId)
    selectedDocument.value = document
    return
  }
  selectedDocument.value = undefined
}

const { data: documents } = useQuery({
  queryKey: ['documents'],
  queryFn: async () => {
    const documents = await documentsService.fetchAll(showArchived.value)
    return documents.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  },
})

const sortField = ref<'name' | 'template_name' | 'created_at' | 'workflow_progress'>('created_at')
const sortOrder = ref<'asc' | 'desc'>('desc')

const sortBy = (field: typeof sortField.value) => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'asc'
  }
}

const sortedDocuments = computed(() => {
  if (!documents.value) return []

  return [...documents.value].sort((a, b) => {
    const compareA = sortField.value === 'created_at' ? new Date(a[sortField.value]).getTime() : a[sortField.value].toLowerCase()
    const compareB = sortField.value === 'created_at' ? new Date(b[sortField.value]).getTime() : b[sortField.value].toLowerCase()

    if (compareA < compareB) return sortOrder.value === 'asc' ? -1 : 1
    if (compareA > compareB) return sortOrder.value === 'asc' ? 1 : -1
    return 0
  })
})

const archiveDocumentMutation = useMutation({
  mutationFn: documentsService.archiveDocument,
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['documents'] })
    successNotification('Document archived')
  },
  onError: (error: Error) => {
    errorNotification((error as AxiosError)?.response?.data?.errors)
  },
})

const unarchiveDocumentMutation = useMutation({
  mutationFn: documentsService.unarchiveDocument,
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['documents'] })
    successNotification('Document unarchived')
  },
  onError: (error: Error) => {
    errorNotification((error as AxiosError)?.response?.data?.errors)
  },
})
</script>
